/**
 * Sign-in page — transition surface, not gate.
 * If persona already selected on landing, shows "Continue as [name]" for 1-click entry.
 * Otherwise shows full persona picker. Email/password collapsed as secondary path.
 * Visual continuity: warm card on navy gradient (no white valley).
 */
"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff } from "lucide-react";
import {
  usePersonaStore,
  useActivePersona,
  usePersonaHasHydrated,
  usePhase1Personas,
  PERSONA_AVATAR_COLORS,
  CUSTOMER_TYPE_KEYS,
} from "@/lib/persona";
import { useT } from "@/lib/locale";
import type { PersonaId } from "@petci/fixtures";

export default function SignInPage() {
  const router = useRouter();
  const t = useT();
  const { setPersona } = usePersonaStore();
  const activePersona = useActivePersona();
  const hasHydrated = usePersonaHasHydrated();
  const personas = usePhase1Personas();
  const activeId = usePersonaStore((s) => s.activeId);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const hasActivePersona = hasHydrated;
  const typeKey = CUSTOMER_TYPE_KEYS[activePersona.customerType] ?? "toolbar.personaTypeResidential";

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError(t("signin.errorEmpty"));
      return;
    }
    setError(null);
    setLoading(true);
    await new Promise((r) => setTimeout(r, 600));
    setLoading(false);
    router.push("/home");
  }

  function handlePersonaPick(id: PersonaId) {
    setPersona(id);
    router.push("/home");
  }

  function handleContinue() {
    router.push("/home");
  }

  return (
    <div className="w-full max-w-md mx-auto space-y-6">
      {/* Card container — warm surface on navy */}
      <div className="bg-[#faf6f1] rounded-2xl shadow-xl p-6 sm:p-8 space-y-6">
        <header className="text-center">
          <h1 className="font-display text-2xl font-bold text-neutral-900 mb-1">
            {hasActivePersona
              ? t("signin.welcomeBack", { name: (activePersona.contactName.split(" ")[0] ?? activePersona.contactName) })
              : t("signin.heading")
            }
          </h1>
          {hasActivePersona ? (
            <p className="text-neutral-500 text-sm">
              {t(typeKey)} · {activePersona.serviceAddress.island.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
            </p>
          ) : (
            <p className="text-neutral-500 text-sm">
              {t("signin.subtitle")}
            </p>
          )}
        </header>

        {/* Continue button — shown when persona already selected */}
        {hasActivePersona && (
          <button
            type="button"
            onClick={handleContinue}
            className="btn-primary w-full text-base py-3"
          >
            {t("signin.continueAs", { name: (activePersona.contactName.split(" ")[0] ?? activePersona.contactName) })}
          </button>
        )}

        {/* Persona cards */}
        <div>
          {hasActivePersona && (
            <p className="text-xs text-neutral-400 text-center mb-3">
              {t("signin.orChooseDifferent")}
            </p>
          )}
          <ul className="grid grid-cols-4 gap-2" role="list">
            {personas.map((p) => {
              const isActive = hasHydrated && p.id === activeId;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => handlePersonaPick(p.id)}
                    className={[
                      "w-full flex flex-col items-center gap-1.5 rounded-xl px-2 py-3 transition-all duration-fast",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary",
                      isActive
                        ? "border-2 border-brand-accent bg-brand-accent/5"
                        : "border border-neutral-200 hover:border-brand-primary hover:bg-brand-primary/5",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold",
                        PERSONA_AVATAR_COLORS[p.id] ?? "bg-neutral-300 text-white",
                      ].join(" ")}
                      aria-hidden
                    >
                      {p.contactName.charAt(0)}
                    </span>
                    <span className="text-xs font-medium text-neutral-900">
                      {p.contactName.split(" ")[0]}
                    </span>
                    <span className="text-[10px] text-neutral-500">
                      {t(CUSTOMER_TYPE_KEYS[p.customerType] ?? "toolbar.personaTypeResidential")}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Email/password — secondary, collapsed */}
      <details className="group">
        <summary className="cursor-pointer text-center text-xs text-white/50 hover:text-white/70 transition-colors list-none [&::-webkit-details-marker]:hidden">
          {t("signin.useCredentials")}
        </summary>
        <div className="mt-4">
          <form onSubmit={handleSubmit} className="bg-[#faf6f1] rounded-2xl shadow-xl p-6 space-y-5" noValidate>
            <p className="text-xs text-neutral-400 text-center">{t("signin.credentialsHint")}</p>

            <div className="space-y-1">
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700">
                {t("signin.emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1">
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700">
                {t("signin.passwordLabel")}
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 pr-10 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-500"
                >
                  {showPassword ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
                </button>
              </div>
            </div>

            {error && (
              <p role="alert" className="text-sm text-semantic-error">{error}</p>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full" aria-busy={loading}>
              {loading ? t("signin.signingIn") : t("signin.signInButton")}
            </button>
          </form>
        </div>
      </details>
    </div>
  );
}
